import { ColumnDef } from "@tanstack/react-table";
import { PersonEventRegistration } from "../types";
import { renderEventStatus } from "@/pages/events/_utils";
import { CellContext } from "@tanstack/react-table";
import { renderBillingStatus } from "@/pages/events/_utils";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useNavigate } from "@/router";
import { toFormat } from "@/lib/dateHelpers";
import { buildTwoLineColumn, buildExpandableCountColumn, buildActionColumn } from "@/components/Table";
import { getEventTypeName } from "@/pages/events/[eventSlug]/utils";

interface GetColumnsProps {
  setMenu: (menu: TableRowMenu) => void;
  navigate: ReturnType<typeof useNavigate>;
  pgaId: string;
}

export const getColumns = ({
  setMenu,
  navigate,
  pgaId
}: GetColumnsProps): ColumnDef<PersonEventRegistration, any>[] => [
  buildTwoLineColumn<PersonEventRegistration>({
    accessorKey: "eventTitle",
    header: "Event Name",
    getPrimaryText: (row) => row.eventTitle,
    getSecondaryText: (row) => getEventTypeName(row) ?? "",
    secondaryLabel: "Type",
    size: 280
  }),
  {
    accessorKey: "startsAt",
    header: "Start Date",
    enableSorting: false,
    cell: ({ row }: { row: { original: PersonEventRegistration } }) => {
      return <div>{toFormat(row.original.startsAt)}</div>;
    }
  },
  {
    accessorKey: "endsAt",
    header: "End Date",
    enableSorting: false,
    cell: ({ row }: { row: { original: PersonEventRegistration } }) => {
      return <div>{toFormat(row.original.endsAt)}</div>;
    }
  },
  buildExpandableCountColumn<PersonEventRegistration>({
    accessorKey: "sessions",
    header: "Sessions",
    getCount: (row) => row.sessions?.length || 0
  }),
  {
    accessorKey: "facility",
    header: "Location",
    enableSorting: false
  },
  {
    accessorKey: "registeredAt",
    header: "Registration",
    enableSorting: false
  },
  {
    accessorKey: "totalPaid",
    header: "Amount Paid",
    enableSorting: false,
    cell: ({ row }: { row: { original: PersonEventRegistration } }) => {
      return row.original.totalPaid || null;
    }
  },
  {
    cell: (cellProps: CellContext<any, unknown>) => {
      const { orderStatus } = cellProps.row.original;
      return cellProps.row.original.orderId ? <>{renderBillingStatus(orderStatus, "small")}</> : null;
    },
    id: "orderStatus",
    header: "Order Status"
  },
  {
    accessorKey: "status",
    header: "Event Stage",
    enableSorting: false,
    cell: ({ row }) => <>{renderEventStatus(row.original.status)}</>,
    meta: {
      headerClassName: "hide-header-in-column"
    }
  },
  buildActionColumn<PersonEventRegistration>({
    getActions: (row) => {
      const actions: Action[] = [
        {
          text: "View Registration",
          onClick: () =>
            navigate(
              `/events/${row.eventSlug}/registrations/${row.id}?return_to=/people/${pgaId}/events/registrations` as any
            )
        },
        {
          text: "View Event",
          onClick: () => navigate(`/events/${row.eventSlug}` as any)
        }
      ];

      if (row.orderId) {
        actions.push({
          text: "View Order",
          onClick: () => navigate(`/billing/orders/${row.orderId}` as any)
        });
      }

      return actions;
    },
    actionId,
    setMenu
  })
];
